import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as RentalActions from './equipment-rental.actions';
import { EquipmentRentalService } from '../../../../core/services/gse/equipment-rental.service';
import { ToastActions } from '../../../toast/toast.actions';

@Injectable()
export class EquipmentRentalEffects {
  private actions$ = inject(Actions);
  private rentalService = inject(EquipmentRentalService);
  private store = inject(Store);

  loadRentals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.loadRentals),
      switchMap(({ params }) =>
        this.rentalService.loadRentals(params).pipe(
          map((res) =>
            RentalActions.loadRentalsSuccess({ data: res.data, totalRecords: res.totalRecords })
          ),
          catchError((err) =>
            of(RentalActions.loadRentalsFailure({ error: err?.error?.message || 'Failed to load rentals' }))
          )
        )
      )
    )
  );

  createRental$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.createRental),
      switchMap(({ record }) =>
        this.rentalService.createRental(record).pipe(
          map(() => RentalActions.createRentalSuccess()),
          catchError((err) =>
            of(RentalActions.createRentalFailure({ error: err?.error?.message || 'Failed to create rental' }))
          )
        )
      )
    )
  );

  createRentalSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.createRentalSuccess),
      tap(() =>
        this.store.dispatch(
          ToastActions.showSuccessToast({ summary: 'Success', detail: 'Equipment rental created successfully' })
        )
      )
    ),
    { dispatch: false }
  );

  createRentalFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.createRentalFailure),
      tap(({ error }) =>
        this.store.dispatch(
          ToastActions.showErrorToast({ summary: 'Error', detail: error || 'Failed to create rental' })
        )
      )
    ),
    { dispatch: false }
  );

  updateRental$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.updateRental),
      switchMap(({ id, record }) =>
        this.rentalService.updateRental(id, record).pipe(
          map(() => RentalActions.updateRentalSuccess()),
          catchError((err) =>
            of(RentalActions.updateRentalFailure({ error: err?.error?.message || 'Failed to update rental' }))
          )
        )
      )
    )
  );

  updateRentalSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.updateRentalSuccess),
      tap(() =>
        this.store.dispatch(
          ToastActions.showSuccessToast({ summary: 'Success', detail: 'Equipment rental updated successfully' })
        )
      )
    ),
    { dispatch: false }
  );

  updateRentalFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.updateRentalFailure),
      tap(({ error }) =>
        this.store.dispatch(
          ToastActions.showErrorToast({ summary: 'Error', detail: error || 'Failed to update rental' })
        )
      )
    ),
    { dispatch: false }
  );

  deleteRental$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.deleteRental),
      switchMap(({ id }) =>
        this.rentalService.deleteRental(id).pipe(
          map(() => RentalActions.deleteRentalSuccess()),
          catchError((err) =>
            of(RentalActions.deleteRentalFailure({ error: err?.error?.message || 'Failed to delete rental' }))
          )
        )
      )
    )
  );

  deleteRentalSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.deleteRentalSuccess),
      tap(() =>
        this.store.dispatch(
          ToastActions.showSuccessToast({ summary: 'Success', detail: 'Equipment rental deleted successfully' })
        )
      )
    ),
    { dispatch: false }
  );

  deleteRentalFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.deleteRentalFailure),
      tap(({ error }) =>
        this.store.dispatch(
          ToastActions.showErrorToast({ summary: 'Error', detail: error || 'Failed to delete rental' })
        )
      )
    ),
    { dispatch: false }
  );

  loadRentalClientsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.loadRentalClientsPage),
      switchMap(({ page, size, search }) =>
        this.rentalService.loadRentalClientsPage(page, size, search).pipe(
          map((result) => RentalActions.loadRentalClientsPageSuccess({ result })),
          catchError((err) =>
            of(RentalActions.loadRentalClientsPageFailure({ error: err?.error?.message || 'Failed to load clients' }))
          )
        )
      )
    )
  );

  loadFboLocationsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentalActions.loadFboLocationsPage),
      switchMap(({ page, size, search }) =>
        this.rentalService.loadFboLocationsPage(page, size, search).pipe(
          map((result) => RentalActions.loadFboLocationsPageSuccess({ result })),
          catchError((err) =>
            of(RentalActions.loadFboLocationsPageFailure({ error: err?.error?.message || 'Failed to load locations' }))
          )
        )
      )
    )
  );
}
